// Domain + form types for the Lookups tab.

export type LookupValueType =
  | "text"
  | "number"
  | "date"
  | "checkbox"
  | "email"
  | "phone";

export const LOOKUP_VALUE_TYPE_OPTIONS: {
  value: LookupValueType;
  label: string;
}[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "checkbox", label: "Checkbox" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
];

export const humanizeLookupValueType = (v: string): string =>
  LOOKUP_VALUE_TYPE_OPTIONS.find((o) => o.value === v)?.label ?? v;

export interface QuestionOption {
  key: string;
  value: string;
}

export interface IndexQuestion {
  slug: string;
  name: string;
  options: QuestionOption[];
}

export type LookupTable = Record<string, string | Record<string, string>>;

export interface Lookup {
  id: string;
  name: string;
  slug: string;
  index1: string;
  index2: string;
  valueType: LookupValueType;
  lookups: LookupTable;
}

export interface LookupFormValues {
  name: string;
  valueType: LookupValueType;
  index1: string;
  index2: string;
  lookups: LookupTable;
}

export const emptyLookupForm: LookupFormValues = {
  name: "",
  valueType: "number",
  index1: "",
  index2: "",
  lookups: {},
};

// Build the saved lookups table from the form's working `lookups`, keyed by the
// CURRENT index questions' option values only (prunes stale keys) and dropping
// empty cells. `index2Options` null => 1-D.
export const buildLookupTable = (
  formLookups: LookupTable,
  index1Options: { value: string }[],
  index2Options: { value: string }[] | null
): LookupTable => {
  const out: LookupTable = {};
  if (!index2Options) {
    index1Options.forEach((o1) => {
      const v = (formLookups[o1.value] as string) ?? "";
      if (v !== "") out[o1.value] = v;
    });
    return out;
  }
  index1Options.forEach((o1) => {
    const row = (formLookups[o1.value] as Record<string, string>) ?? {};
    const builtRow: Record<string, string> = {};
    index2Options.forEach((o2) => {
      const v = row[o2.value] ?? "";
      if (v !== "") builtRow[o2.value] = v;
    });
    if (Object.keys(builtRow).length > 0) out[o1.value] = builtRow;
  });
  return out;
};
